import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  MoECapacitorCore,
  MoEAppStatus,
  MoEProperties,
  MoEInitConfig,
} from 'capacitor-moengage-core';
import { environment } from '../../../environments/environment';
import Moengage, { env as SDK_ENVIRONMENT } from '@moengage/web-sdk';
import {
  MoEngageEventMap,
  MoEngageEventName,
  MoEngageUserAttributeMap,
  MoEngageUserAttributeName,
} from './moengage-model';
import { Profile } from '../../profile/model/profile.model';
import { Training } from '../../qualifications/models/training.model';
import { PREMIUM_LEVEL_TO_DRIVER_STATUS } from '../constants';
import { LoggerService } from 'src/app/services/logger.service';
import {
  MyPlannedTransfer,
  Steps,
} from 'src/app/my-transfers/model/my-transfer.model';

@Injectable({ providedIn: 'root' })
export class MoEngageService {
  private appId = environment.moEngage.appId;
  private isWeb = Capacitor.getPlatform() === 'web';
  private baseUrl = environment.movacarProUrl.replace(/\/$/, '');

  private logger = new LoggerService(MoEngageService.name);

  constructor(private router: Router) {}

  async initialize(isFirstInstall: boolean) {
    if (this.isWeb) {
      try {
        Moengage.initialize({
          app_id: this.appId,
          cluster: 'DC_2',
          env: environment.production
            ? SDK_ENVIRONMENT.LIVE
            : SDK_ENVIRONMENT.TEST,
          logLevel: environment.moEngage.enableDebugLogs ? 1 : 0,
          swPath: '/moesw.js',
        });
      } catch (e) {
        this.logger.error({
          message: 'Web initialize error',
          identifier: 'moengage_service_web_initialize_error',
          method: 'initialize',
          value: e,
        });
      }

      return;
    }

    try {
      this.logger.info({
        message: 'Native initialize start',
        identifier: 'moengage_service_native_initialize_start',
        method: 'initialize',
        value: { isFirstInstall },
      });

      const initConfig: MoEInitConfig = {
        analyticsConfig: { shouldTrackUserAttributeBooleanAsNumber: false },
      };

      await MoECapacitorCore.initialize({ appId: this.appId, initConfig });

      await MoECapacitorCore.setAppStatus({
        appStatus: isFirstInstall ? MoEAppStatus.INSTALL : MoEAppStatus.UPDATE,
        appId: this.appId,
      });

      this.logger.info({
        message: 'Native initialize done',
        identifier: 'moengage_service_native_initialize_done',
        method: 'initialize',
      });
    } catch (e) {
      this.logger.error({
        message: 'Native initialize error',
        identifier: 'moengage_service_native_initialize_error',
        method: 'initialize',
        value: e,
      });
    }
  }

  async identifyUser(uniqueId: string) {
    if (this.isWeb) {
      try {
        await Moengage.identifyUser(uniqueId);
        Moengage.call_web_push();
      } catch (e) {
        this.logger.error({
          message: 'Web identifyUser error',
          identifier: 'moengage_service_web_identify_user_error',
          method: 'identifyUser',
          value: e,
        });
      }

      return;
    }

    try {
      this.logger.info({
        message: 'Native identifyUser',
        identifier: 'moengage_service_native_identify_user',
        method: 'identifyUser',
        value: { uniqueId },
      });

      await MoECapacitorCore.setUniqueId({ uniqueId, appId: this.appId });

      this.logger.info({
        message: 'Native identifyUser done',
        identifier: 'moengage_service_native_identify_user_done',
        method: 'identifyUser',
      });
    } catch (e) {
      this.logger.error({
        message: 'Native identifyUser error',
        identifier: 'moengage_service_native_identify_user_error',
        method: 'identifyUser',
        value: e,
      });
    }
  }

  async setUserAttribute<K extends MoEngageUserAttributeName>(
    name: K,
    value: MoEngageUserAttributeMap[K],
  ) {
    if (this.isWeb) {
      await this.setWebUserAttribute(name, value);

      return;
    }

    await this.setNativeUserAttribute(name, value);
  }

  async trackEvent<T extends MoEngageEventName>(
    eventName: T,
    ...args: MoEngageEventMap[T] extends Record<string, never>
      ? []
      : [attributes: MoEngageEventMap[T]]
  ) {
    const attributes = args[0] as
      | Record<string, string | number | boolean | Date>
      | undefined;

    if (this.isWeb) {
      const webAttributes = attributes
        ? Object.fromEntries(
            Object.entries(attributes).map(([k, v]) => [
              k,
              v instanceof Date
                ? new Date(v.getTime() + v.getTimezoneOffset() * 60000)
                : v,
            ]),
          )
        : {};

      await Moengage.track_event(eventName, webAttributes);

      return;
    }

    try {
      const eventAttributes: MoEProperties | undefined = attributes
        ? {
            generalAttributes: Object.entries(attributes)
              .filter(
                ([, value]) =>
                  value !== undefined &&
                  value !== null &&
                  !(value instanceof Date),
              )
              .map(([name, value]) => ({
                name,
                value: Array.isArray(value)
                  ? (value as string[])
                  : (value as string | number | boolean),
              })),

            dateTimeAttributes: Object.entries(attributes)
              .filter(([, value]) => value instanceof Date)
              .map(([name, value]) => ({
                name,
                value: (value as Date).toISOString(),
              })),
          }
        : undefined;

      this.logger.info({
        message: 'Native trackEvent',
        identifier: 'moengage_service_native_track_event',
        method: 'trackEvent',
        value: { eventName, eventAttributes },
      });

      await MoECapacitorCore.trackEvent({
        eventName,
        eventAttributes,
        appId: this.appId,
      });

      this.logger.info({
        message: 'Native trackEvent success',
        identifier: 'moengage_service_native_track_event_success',
        method: 'trackEvent',
        value: { eventName },
      });
    } catch (e) {
      this.logger.error({
        message: 'Native trackEvent error',
        identifier: 'moengage_service_native_track_event_error',
        method: 'trackEvent',
        value: { eventName, error: e },
      });
    }
  }

  trackPageViewed() {
    const page_url = this.baseUrl + this.router.url;
    this.trackEvent('page_viewed', { page_url });
  }

  trackInspection(
    plannedTransfer: MyPlannedTransfer,
    step: Steps,
    eventName: 'inspection_started' | 'inspection_completed',
  ) {
    this.trackEvent(eventName, {
      reference_key: plannedTransfer.transfer.referenceKey,
      pickup: plannedTransfer.transfer.origin.city,
      dropoff: plannedTransfer.transfer.destination.city,
      planned_pickup_time: new Date(plannedTransfer.plannedPickupDate),
      planned_dropoff_time: new Date(plannedTransfer.plannedDropoffDate),
      step: step,
    });
  }

  async registerForPush() {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    try {
      await MoECapacitorCore.registerForPush();
    } catch (e) {
      this.logger.error({
        message: 'registerForPush error',
        identifier: 'moengage_service_register_for_push_error',
        method: 'registerForPush',
        value: e,
      });
    }
  }

  async setupAndroidPush() {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    try {
      await MoECapacitorCore.setupNotificationChannelsAndroid();
      await MoECapacitorCore.requestPushPermissionAndroid();
    } catch (e) {
      this.logger.error({
        message: 'setupAndroidPush error',
        identifier: 'moengage_service_setup_android_push_error',
        method: 'setupAndroidPush',
        value: e,
      });
    }
  }

  async syncUserProfile(profile: Profile, basicTrainings: Training[]) {
    const allBasicCompleted =
      basicTrainings.length > 0 && basicTrainings.every((t) => t.isCompleted);

    if (!profile.salesforceId) {
      this.logger.error({
        message: 'Cannot identify user: missing salesforceId',
        identifier: 'moengage_service_sync_user_profile_no_salesforce_id',
        method: 'syncUserProfile',
      });

      return;
    }

    await this.identifyUser(profile.salesforceId);

    if (!this.isWeb) {
      this.logger.info({
        message: 'Native: waiting for SDK to settle after identify',
        identifier: 'moengage_service_sync_user_profile_sdk_settle',
        method: 'syncUserProfile',
      });

      await new Promise<void>((resolve) => setTimeout(resolve, 500));

      this.logger.info({
        message: 'Native: starting to set user attributes',
        identifier: 'moengage_service_sync_user_profile_set_attributes_start',
        method: 'syncUserProfile',
      });
    }

    if (profile.email) {
      await this.setUserAttribute('user_email', profile.email);
    }

    if (profile.firstName) {
      await this.setUserAttribute('user_first_name', profile.firstName);
    }

    if (profile.lastName) {
      await this.setUserAttribute('user_last_name', profile.lastName);
    }

    if (profile.phoneNumber) {
      await this.setUserAttribute('user_mobile_phone', profile.phoneNumber);
    }

    if (profile.role) {
      await this.setUserAttribute('user_role', profile.role);
    }

    if (profile.isApproved) {
      await this.setUserAttribute('is_approved', profile.isApproved);
    }

    if (profile.createdAt) {
      await this.setUserAttribute('registered_since', profile.createdAt);
    }

    if (profile.language) {
      await this.setUserAttribute('user_language', profile.language);
    }

    if (profile.mailingAddress?.city) {
      await this.setUserAttribute('user_city', profile.mailingAddress.city);
    }

    if (profile.mailingAddress?.country) {
      await this.setUserAttribute(
        'user_country',
        profile.mailingAddress.country,
      );
    }

    if (profile.dateOfBirth) {
      const dateOfBirth = new Date(profile.dateOfBirth);

      await this.setUserAttribute(
        'user_date_of_birth',
        dateOfBirth.toISOString(),
      );
    }

    if (profile.drivingLicenseIssuedAt) {
      const drivingLicenseIssuedAt = new Date(profile.drivingLicenseIssuedAt);

      await this.setUserAttribute(
        'driver_licence_since',
        drivingLicenseIssuedAt.toISOString(),
      );
    }

    if (profile.services) {
      await this.setUserAttribute(
        'red_plate',
        profile.services.redNumberPlateTransfer,
      );
    }

    if (profile.transportTypes) {
      await this.setUserAttribute(
        'transport_on_trailer',
        profile.transportTypes.transportOnTrailer,
      );

      await this.setUserAttribute(
        'transport_axel',
        profile.transportTypes.transportPerAxel,
      );
    }

    if (profile.vehicleTypes) {
      await this.setUserAttribute('truck_12', profile.vehicleTypes.truck12T);

      await this.setUserAttribute(
        'truck_7_5',
        profile.vehicleTypes.truck7Dot5T,
      );
    }

    const status = PREMIUM_LEVEL_TO_DRIVER_STATUS[profile.premiumPartnerLevel];

    if (status) {
      await this.setUserAttribute('driver_status', status);
    }

    if (profile.germanLanguageLevel) {
      await this.setUserAttribute(
        'german_language_level',
        profile.germanLanguageLevel,
      );
    }

    if (profile.germanLanguageApprovedAt) {
      const approvedAt = new Date(profile.germanLanguageApprovedAt);

      await this.setUserAttribute(
        'german_language_approved',
        approvedAt.toISOString(),
      );
    }

    await this.setUserAttribute('basic_trainings_completed', allBasicCompleted);
  }

  async resetUser() {
    if (this.isWeb) {
      try {
        await Moengage.destroy_session();
      } catch (e) {
        this.logger.error({
          message: 'Web resetUser error',
          identifier: 'moengage_service_web_reset_user_error',
          method: 'resetUser',
          value: e,
        });
      }

      return;
    }

    try {
      await MoECapacitorCore.logoutUser({ appId: this.appId });
    } catch (e) {
      this.logger.error({
        message: 'Native resetUser error',
        identifier: 'moengage_service_native_reset_user_error',
        method: 'resetUser',
        value: e,
      });
    }
  }

  private async setNativeUserAttribute<K extends MoEngageUserAttributeName>(
    name: K,
    value: MoEngageUserAttributeMap[K],
  ) {
    this.logger.info({
      message: 'Native setUserAttribute',
      identifier: 'moengage_service_native_set_user_attribute',
      method: 'setNativeUserAttribute',
      value: { name, value },
    });

    try {
      switch (name) {
        case 'user_email':
          await MoECapacitorCore.setEmailId({
            emailId: value as string,
            appId: this.appId,
          });
          break;
        case 'user_first_name':
          await MoECapacitorCore.setFirstName({
            firstName: value as string,
            appId: this.appId,
          });
          break;
        case 'user_last_name':
          await MoECapacitorCore.setLastName({
            lastName: value as string,
            appId: this.appId,
          });
          break;
        case 'user_mobile_phone':
          await MoECapacitorCore.setMobileNumber({
            mobileNumber: value as string,
            appId: this.appId,
          });
          break;
        case 'user_date_of_birth':
          await MoECapacitorCore.setBirthDate({
            birthdate: value as string,
            appId: this.appId,
          });
          break;
        default:
          await MoECapacitorCore.setUserAttribute({
            name,
            value,
            appId: this.appId,
          });
      }

      this.logger.info({
        message: 'Native setUserAttribute done',
        identifier: 'moengage_service_native_set_user_attribute_done',
        method: 'setNativeUserAttribute',
        value: { name },
      });
    } catch (e) {
      this.logger.error({
        message: 'Native setUserAttribute error',
        identifier: 'moengage_service_native_set_user_attribute_error',
        method: 'setNativeUserAttribute',
        value: { name, error: e },
      });
    }
  }

  private async setWebUserAttribute<K extends MoEngageUserAttributeName>(
    name: K,
    value: MoEngageUserAttributeMap[K],
  ) {
    switch (name) {
      case 'user_email':
        await Moengage.add_email(value as string);
        break;
      case 'user_first_name':
        await Moengage.add_first_name(value as string);
        break;
      case 'user_last_name':
        await Moengage.add_last_name(value as string);
        break;
      case 'user_mobile_phone':
        await Moengage.add_mobile(value as string);
        break;
      case 'user_date_of_birth':
        await Moengage.add_birthday(new Date(value as string));
        break;
      default:
        await Moengage.add_user_attribute(name, value);
    }
  }
}
